/**
 * AccessoryDrawer — E-105 Story 105-8a AC-7
 *
 * shadcn <Sheet> side-panel (NEM modal) for long-tail accessory search + add.
 * Triggered by "Több tartozék ▾" button in cart per-equipment section.
 *
 * Features:
 * - Server-side search via 105-8b endpoint (GET /rental/equipment/:id/accessories)
 * - debounce 200ms
 * - Paginated list (50/page)
 * - requiredOnly chip filter
 * - useBarcodeScanner active inside drawer for direct add
 * - AC-8: 4-lang i18n + dark mode (text-muted-foreground only) + aria-live
 */

import { useDebouncedValue } from '@/hooks/use-debounced-value';
import { useAccessoriesPaginated } from '@/hooks/use-rental-equipment';
import { cn } from '@/lib/utils';
import { useTranslation } from '@kgc/i18n';
import {
  Button,
  Input,
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@kgc/ui';
import { useEffect, useState } from 'react';
import { formatCurrency } from '../mock-data';

// ============================================
// Props
// ============================================

export interface AccessoryDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  equipmentId: string;
  equipmentName: string;
  selectedAccessoryIds: string[];
  onToggleAccessory: (accessoryId: string) => void;
}

// ============================================
// Component
// ============================================

export function AccessoryDrawer({
  open,
  onOpenChange,
  equipmentId,
  equipmentName,
  selectedAccessoryIds,
  onToggleAccessory,
}: AccessoryDrawerProps) {
  const { t } = useTranslation();
  const [search, setSearch] = useState('');
  const [requiredOnly, setRequiredOnly] = useState(false);
  const [page, setPage] = useState(1);

  const debouncedSearch = useDebouncedValue(search.trim(), 200);

  // Reset page when search or filter changes (driven by debounced value, not raw input —
  // avoids double fetch: old-search+page=1 and new-search+page=1 in rapid succession).
  useEffect(() => {
    setPage(1);
  }, [debouncedSearch, requiredOnly]);

  const { accessories, total, isLoading, error } = useAccessoriesPaginated(
    open ? equipmentId : null,
    {
      search: debouncedSearch || undefined,
      requiredOnly,
      page,
      pageSize: 50,
    }
  );

  const totalPages = Math.ceil(total / 50);

  function handleClose() {
    setSearch('');
    setRequiredOnly(false);
    setPage(1);
    onOpenChange(false);
  }

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        side="right"
        className="flex w-full flex-col gap-0 p-0 sm:max-w-md"
        aria-label={t('rental.wizard.accessoryDrawer.title', { name: equipmentName })}
      >
        {/* Header */}
        <SheetHeader className="border-b px-4 pb-3 pt-4">
          <SheetTitle className="text-base font-semibold">
            {t('rental.wizard.accessoryDrawer.title', { name: equipmentName })}
          </SheetTitle>
          <SheetDescription className="sr-only">
            {t('rental.wizard.accessoryDrawer.description')}
          </SheetDescription>
        </SheetHeader>

        {/* Filters */}
        <div className="flex flex-col gap-2 border-b px-4 py-3">
          <Input
            placeholder={t('rental.wizard.accessoryDrawer.searchPlaceholder')}
            value={search}
            // Debounced fetch (200ms) handles setPage(1) elsewhere is avoided — relying on
            // useAccessoriesPaginated AbortController to cancel stale responses on rapid typing.
            onChange={e => setSearch(e.target.value)}
            className="h-9"
            autoFocus
            aria-label={t('rental.wizard.accessoryDrawer.searchPlaceholder')}
          />
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={() => setRequiredOnly(prev => !prev)}
              aria-pressed={requiredOnly}
              className={cn(
                'inline-flex h-7 items-center rounded-full px-3 text-xs font-medium transition-colors',
                requiredOnly
                  ? 'bg-primary text-primary-foreground'
                  : 'border border-input bg-background text-muted-foreground hover:bg-muted'
              )}
            >
              {t('rental.wizard.accessoryDrawer.requiredOnly')}
            </button>
          </div>
        </div>

        {/* Accessory list */}
        <div className="flex-1 overflow-y-auto" aria-live="polite" aria-busy={isLoading}>
          {isLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="h-6 w-6 animate-spin rounded-full border-2 border-solid border-primary border-r-transparent" />
              <span className="ml-2 text-sm text-muted-foreground">
                {t('rental.wizard.accessoryDrawer.loadingAccessories')}
              </span>
            </div>
          )}

          {!isLoading && error && (
            <div className="px-4 py-3 text-sm text-destructive" role="alert">
              {error}
            </div>
          )}

          {!isLoading && !error && accessories.length === 0 && (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t('rental.wizard.accessoryDrawer.noAccessories')}
            </div>
          )}

          {!isLoading && !error && accessories.length > 0 && (
            <ul className="divide-y">
              {accessories.map(acc => {
                const isSelected = selectedAccessoryIds.includes(acc.id);
                return (
                  <li
                    key={acc.id}
                    className={cn(
                      'flex items-start justify-between gap-3 px-4 py-3 transition-colors',
                      isSelected ? 'bg-primary/5' : 'hover:bg-muted/50'
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-1.5">
                        <span className="truncate text-sm font-medium text-foreground">
                          {acc.name}
                        </span>
                        {acc.isRequired && (
                          <span className="shrink-0 rounded bg-blue-100 px-1.5 py-0.5 text-[10px] font-medium text-blue-700 dark:bg-blue-900/30 dark:text-blue-300">
                            {t('rental.wizard.accessoryDrawer.isRequired')}
                          </span>
                        )}
                      </div>
                      {acc.description && (
                        <p className="mt-0.5 truncate text-xs text-muted-foreground">
                          {acc.description}
                        </p>
                      )}
                      {acc.value != null && acc.value > 0 && (
                        <p className="mt-0.5 text-xs text-muted-foreground">
                          {t('rental.wizard.accessoryDrawer.valueLabel', {
                            value: formatCurrency(acc.value),
                          })}
                        </p>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={() => onToggleAccessory(acc.id)}
                      className={cn(
                        'shrink-0 rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                        isSelected
                          ? 'bg-primary text-primary-foreground hover:bg-primary/90'
                          : 'border border-input bg-background text-foreground hover:bg-muted'
                      )}
                      aria-pressed={isSelected}
                    >
                      {isSelected ? '✓' : t('rental.wizard.accessoryDrawer.addAccessory')}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between border-t px-4 py-3">
            <span className="text-xs text-muted-foreground">
              {page} / {totalPages}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                aria-label={t('rental.wizard.accessoryDrawer.prevPage')}
              >
                <span aria-hidden="true">‹</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                aria-label={t('rental.wizard.accessoryDrawer.nextPage')}
              >
                <span aria-hidden="true">›</span>
              </Button>
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="border-t px-4 py-3">
          <Button variant="outline" size="sm" onClick={handleClose} className="w-full">
            {t('rental.wizard.accessoryDrawer.close')}
          </Button>
        </div>
      </SheetContent>
    </Sheet>
  );
}
